import bind from 'bind-decorator';

// The list of options drawn in place of a native <select> — the single producer of that
// markup, used both by the dropdown of a CustomSelect and by a list rendered open (the
// tariff modal of the cabinet). It hides the select it is built over and writes every
// choice back into it, so the select stays the state and the form field.
//
// Read off an <option>: its class and all of its data attributes (moved onto the option
// drawn for it), data-divider (becomes the is-group-start class) and .placeholder-inner-text
// inside it (the part of the option a placeholder shows on its own). Produced markup:
// button.option[data-value], with is-current on the option of the current value.
export default class OptionList {
  constructor({ $select, $container, isHtml = false, onSelect = null }) {
    this.$select = $select;
    this.$container = $container;
    this.isHtml = isHtml;
    this.onSelect = onSelect;

    this.$select.hide();
    this.render();

    this.$container.on('click', '.option', this.onOptionClick);
  }

  get currentValue() {
    return this.$select.val();
  }

  get $options() {
    return this.$container.find('.option');
  }

  get $currentOption() {
    // option values may contain backslashes (imask), which break CSS attribute selectors
    return this.$select
      .find('option')
      .filter((_index, node) => node.value === this.currentValue)
      .first();
  }

  @bind
  onOptionClick({ currentTarget }) {
    this.selectOption(currentTarget.getAttribute('data-value'));
  }

  selectOption(value) {
    this.$select
      .val(value)
      .trigger('change');

    this.syncCurrentOption();
    this.onSelect?.(value);
  }

  syncCurrentOption() {
    this.$options.removeClass('is-current');

    this.$options
      .filter((_index, node) => node.getAttribute('data-value') === this.currentValue)
      .addClass('is-current');
  }

  // the current option the way a placeholder shows it: an option may wrap the text of its
  // placeholder into .placeholder-inner-text and keep a badge next to it — the placeholder
  // takes that text alone
  renderCurrentOption($target) {
    const [currentOption] = this.$currentOption;
    if (!currentOption) { return $target; }

    this.renderOption(currentOption, $target);

    const $innerText = $target.find('.placeholder-inner-text');
    if ($innerText.length) {
      $target.html($innerText.html());
    }

    return $target;
  }

  destroy() {
    this.$container.off('click', '.option', this.onOptionClick);
    this.$options.remove();
    this.$select.show();
  }

  render() {
    this.$select.find('option').each((_index, node) => {
      const $option = $('<button type="button" class="option" />')
        .toggleClass('is-group-start', node.hasAttribute('data-divider'));

      this.renderOption(node, $option);
      this.$container.append($option.attr('data-value', node.value));
    });

    this.syncCurrentOption();
  }

  // an <option> may contain nothing but text, so a styled option carries its own class and
  // data attributes (a country flag, the scope id of the vue component that rendered it) on
  // the tag itself — they belong to the elements drawn in place of the hidden select
  renderOption(node, $target) {
    if (node.className) {
      $target.addClass(node.className);
    }

    [...node.attributes]
      .filter(({ name }) => name.startsWith('data-') && name !== 'data-divider')
      .forEach(({ name, value }) => $target.attr(name, value));

    return this.isHtml ? $target.html(node.innerHTML) : $target.text(node.innerText);
  }
}
